import axios from 'axios';
import { authErrorEvent } from './api/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/**
 * Category Service
 * Handles media category operations
 */
class CategoryService {
  /**
   * Get authorization headers with access token
   */
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('accessToken');
    const headers: Record<string, string> = {};
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  /**
   * Get all categories
   */
  async getAllCategories() {
    try {
      const response = await axios.get(`${API_BASE_URL}/categories`, {
        headers: this.getAuthHeaders(),
      });
      return response.data.data?.docs || response.data.data || [];
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get categories with pagination
   */
  async listCategories(page: number = 1, limit: number = 10) {
    try {
      const response = await axios.get(`${API_BASE_URL}/categories`, {
        params: { page, limit },
        headers: this.getAuthHeaders(),
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get category by ID
   */
  async getCategoryById(id: string) {
    try {
      const response = await axios.get(`${API_BASE_URL}/categories/${id}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get category by slug
   */
  async getCategoryBySlug(slug: string) {
    try {
      const response = await axios.get(`${API_BASE_URL}/categories/slug/${slug}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create new category
   */
  async createCategory(data: {
    name: string;
    description?: string;
    icon?: string;
    color?: string;
  }) {
    try {
      const response = await axios.post(`${API_BASE_URL}/categories`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update category
   */
  async updateCategory(id: string, data: Partial<{
    name: string;
    description?: string;
    icon?: string;
    color?: string;
    order?: number;
  }>) {
    try {
      const response = await axios.patch(`${API_BASE_URL}/categories/${id}`, data, {
        headers: this.getAuthHeaders(),
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete category
   */
  async deleteCategory(id: string) {
    try {
      const response = await axios.delete(`${API_BASE_URL}/categories/${id}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get default category
   */
  async getDefaultCategory() {
    try {
      const response = await axios.get(`${API_BASE_URL}/categories/default`, {
        headers: this.getAuthHeaders(),
      });
      return response.data.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Error handling
   */
  private handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      const statusCode = error.response?.status;
      const message = error.response?.data?.message || error.message;

      // Handle authentication errors (401/403)
      if (statusCode === 401 || statusCode === 403) {
        // Clear stored tokens
        if (typeof window !== 'undefined') {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
        }

        // Dispatch custom event to notify app
        const event = new CustomEvent('authError', {
          detail: {
            statusCode,
            message: statusCode === 401 ? 'Token expired or invalid' : 'Access forbidden',
            timestamp: new Date().toISOString(),
          },
        });
        authErrorEvent.dispatchEvent(event);

        console.warn(
          `%c🔐 Authentication Error [${statusCode}]`,
          'color: #ff6600; font-weight: bold;',
          'Redirecting to login...'
        );
      }

      return new Error(message);
    }
    return error instanceof Error ? error : new Error('Unknown error');
  }
}

const categoryService = new CategoryService();
export default categoryService;
